/**
 * When a browser / Android-app tab may reload itself onto a new build
 * (F-FE-N191). Pure, so it is unit-tested in node; the runtime that feeds it
 * lives in `webUpdate.ts`.
 *
 * Background: the service worker serves the precached bundle on every load and
 * a browser only looks for a newer worker on a real navigation. A POS tab that
 * stays open for days never navigates, so it ran a week-old bundle until
 * somebody hard-refreshed — and even a found update only installed and then
 * waited for every tab to close. `webUpdate.ts` now checks on a timer; this file
 * decides when acting on a found update is safe.
 */

/** How long nobody has touched the screen before an update applies on its own. */
export const AUTO_APPLY_IDLE_MS = 2 * 60_000

/** Background check cadence while the tab is open. */
export const CHECK_INTERVAL_MS = 10 * 60_000

/** Focus / visibility / online events re-check, but never more often than this. */
export const MIN_CHECK_GAP_MS = 60_000

/**
 * The server has carried a newer build for this long and the service worker
 * still has not moved onto it — treat the worker as stuck and reload past it.
 */
export const SW_STUCK_MS = 30 * 60_000

/**
 * Screens that exist to make a noise. A reload leaves the browser's audio
 * locked until someone touches the screen, so an unattended KDS or online-
 * orders monitor that reloaded itself would take the next order silently.
 * These only ever show the banner; a person taps it.
 */
export const LISTENING_SCREENS = ['/kitchen', '/admin/kitchen', '/admin/online-orders'] as const

export function isListeningScreen(pathname: string): boolean {
  return LISTENING_SCREENS.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

/** True when the server's build id names a build other than the one running. */
export function isServerAhead(localBuildId: string, remoteBuildId: unknown): boolean {
  return typeof remoteBuildId === 'string' && remoteBuildId !== '' && remoteBuildId !== localBuildId
}

export function shouldCheckNow(lastCheckAt: number | null, now: number): boolean {
  return lastCheckAt === null || now - lastCheckAt >= MIN_CHECK_GAP_MS
}

export type ApplyMode =
  /** A new worker is installed and waiting: tell it to take over, then reload. */
  | 'activate'
  /** No worker controls the page: index.html is no-cache, so a reload lands on the new build. */
  | 'reload'
  /** The worker never moved: unregister it, then reload straight from the network. */
  | 'bypass'

export interface ReadinessInput {
  /** A new service worker is installed and waiting (vite-plugin-pwa onNeedRefresh). */
  workerWaiting: boolean
  /**
   * The new worker already took control (another tab activated it): this tab
   * still runs the old bundle and only needs a reload — at its own safe moment.
   */
  workerActivated: boolean
  /** When /version.json first disagreed with the running build, else null. */
  serverAheadSince: number | null
  /** A service worker controls this page. */
  controlled: boolean
  now: number
}

/** How to get onto the new build, or null when there is nothing (yet) to apply. */
export function updateReadiness(i: ReadinessInput): ApplyMode | null {
  if (i.workerActivated) return 'reload'
  if (i.workerWaiting) return 'activate'
  if (i.serverAheadSince === null) return null
  // No worker: index.html is served no-cache, so a plain reload is enough.
  if (!i.controlled) return 'reload'
  // Give the worker time to install the new build itself before going around it.
  return i.now - i.serverAheadSince >= SW_STUCK_MS ? 'bypass' : null
}

export interface AutoApplyInput {
  ready: boolean
  /** restartGuard's first active reason (e.g. an open checkout), or null. */
  blocker: string | null
  /** A react-query mutation is in flight. */
  mutating: boolean
  idleMs: number
  online: boolean
  pathname: string
}

/** May the update apply without anyone tapping it? */
export function autoApplyAllowed(i: AutoApplyInput): boolean {
  return (
    i.ready &&
    i.blocker === null &&
    !i.mutating &&
    i.online &&
    i.idleMs >= AUTO_APPLY_IDLE_MS &&
    !isListeningScreen(i.pathname)
  )
}

/** Reason the manual "Update now" tap is refused right now, or null. */
export function manualApplyBlocker(i: Pick<AutoApplyInput, 'blocker' | 'mutating' | 'online'>): string | null {
  if (i.blocker) return i.blocker
  if (i.mutating) return 'Something is still saving'
  if (!i.online) return 'Waiting for the connection'
  return null
}
